import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtMoney, errMsg, sym } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Trash2, CreditCard as CardIcon, Edit2, Send, Banknote, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function CardsPage() {
  const [cards, setCards] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [paying, setPaying] = useState(null);

  const load = async () => {
    try {
      const [c, a] = await Promise.all([api.get("/cards"), api.get("/accounts")]);
      setCards(c.data); setAccounts(a.data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("¿Eliminar tarjeta?")) return;
    try { await api.delete(`/cards/${id}`); toast.success("Eliminada"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6" data-testid="cards-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Mis productos</div>
          <h1 className="text-2xl sm:text-3xl font-bold mt-1">Tarjetas</h1>
        </div>
        <button onClick={() => setOpen(true)} data-testid="add-card-btn"
          className="flex items-center gap-2 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white px-4 py-2.5 rounded-xl font-medium text-sm shadow-[0_8px_30px_-10px_rgba(139,92,246,0.6)]">
          <Plus className="w-4 h-4"/> Nueva tarjeta
        </button>
      </div>

      {cards.length === 0 ? (
        <div className="card-surface p-12 text-center">
          <CardIcon className="w-10 h-10 text-slate-600 mx-auto"/>
          <div className="mt-3 text-slate-400 text-sm">Aún no has agregado tarjetas.</div>
          <button onClick={() => setOpen(true)} data-testid="empty-add-card-btn" className="mt-3 text-sm text-violet-300 hover:text-violet-200 font-medium">Agregar primera →</button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {cards.map(c => (
            <CardItem key={c.id} card={c} accounts={accounts} onDelete={() => del(c.id)} onEdit={() => setEditing(c)} onPay={() => setPaying(c)}/>
          ))}
        </div>
      )}

      <AddCardDialog open={open} onOpenChange={setOpen} accounts={accounts} onCreated={load}/>
      {editing && <EditCardDialog card={editing} onClose={() => setEditing(null)} onSaved={() => { load(); setEditing(null); }}/>}
      {paying && <PayCardDialog card={paying} accounts={accounts} cards={cards} onClose={() => setPaying(null)} onPaid={() => { load(); setPaying(null); }}/>}
    </div>
  );
}

function CardItem({ card, accounts, onDelete, onEdit, onPay }) {
  const linkedAcct = accounts.find(a => a.id === card.account_id);
  const isCredit = card.kind === "credit";
  const usedPct = isCredit && card.limit ? Math.min(100, (card.balance_used / card.limit) * 100) : 0;
  const owed = isCredit ? cardOwed(card) : 0;
  return (
    <div className="relative rounded-2xl overflow-hidden group" data-testid={`card-item-${card.id}`}>
      {/* Card visual */}
      <div className="p-5 h-52 flex flex-col justify-between relative" style={{
        background: `linear-gradient(135deg, ${card.color || "#8B5CF6"} 0%, #1a1825 100%)`,
        boxShadow: `0 12px 40px -16px ${card.color || "#8B5CF6"}66`,
      }}>
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10 blur-2xl"/>
        <div className="relative flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/70 font-bold">
              {card.bank || card.brand}
            </div>
            <div className="mt-1 text-base font-semibold text-white">{card.name}</div>
            {card.bank && <div className="text-[10px] text-white/60 mt-0.5">{card.brand}</div>}
          </div>
          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold ${isCredit ? "bg-white/20 text-white" : "bg-emerald-500/30 text-emerald-100"}`}>
            {isCredit ? "Crédito" : "Débito"}
          </span>
        </div>
        <div className="relative">
          <div className="font-mono text-xl tracking-[0.25em] text-white/95">•••• {card.last4}</div>
          {isCredit && (card.closing_day || card.payment_due_day) && (
            <div className="mt-1 flex gap-3 text-[10px] text-white/70">
              {card.closing_day && <span>Corte: día {card.closing_day}</span>}
              {card.payment_due_day && <span>Pago: día {card.payment_due_day}</span>}
            </div>
          )}
          <div className="mt-3 flex items-end justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/60 font-bold">{isCredit ? "Disponible" : "Cuenta"}</div>
              <div className="text-sm font-semibold text-white">
                {isCredit
                  ? fmtMoney((card.limit || 0) - (card.balance_used || 0), card.currency === "DOP" ? "RD$" : `${card.currency} `)
                  : (linkedAcct?.name || "—")}
              </div>
            </div>
            <button onClick={onDelete} data-testid={`delete-card-${card.id}`} className="opacity-0 group-hover:opacity-100 text-white/70 hover:text-rose-300 transition-all">
              <Trash2 className="w-4 h-4"/>
            </button>
          </div>
          {isCredit && card.limit > 0 && (
            <div className="mt-2 h-1 bg-white/20 rounded-full overflow-hidden">
              <div className="h-full bg-white" style={{ width: `${usedPct}%` }}/>
            </div>
          )}
          {isCredit && card.currency_mode === "independent" && card.balance_used_by_currency && (
            <div className="mt-2 grid grid-cols-2 gap-1.5 text-[10px]">
              {Object.entries(card.balance_used_by_currency).map(([cur, used]) => {
                const lim = (card.multi_currency_limits || {})[cur] || 0;
                return (
                  <div key={cur} className="bg-white/10 rounded-md px-2 py-1">
                    <div className="text-white/70 font-bold">{cur}</div>
                    <div className="text-white font-mono">{fmtMoney(used)} / {fmtMoney(lim)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {/* Pago del estado de cuenta — acción principal de una tarjeta de crédito,
          así que va siempre visible, no escondida tras :hover como editar/borrar. */}
      {isCredit && (
        <div className="flex items-center justify-between gap-3 bg-white/5 border-x border-b border-white/10 rounded-b-2xl px-4 py-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Saldo usado</div>
            <div className={`text-sm font-bold ${owed > 0 ? "text-rose-300" : "text-emerald-300"}`}>
              {fmtMoney(owed, sym(card.currency))}
            </div>
          </div>
          <button onClick={onPay} disabled={owed <= 0} data-testid={`pay-card-${card.id}`}
            className="shrink-0 flex items-center gap-1.5 bg-gradient-to-r from-emerald-500 to-violet-700 disabled:from-white/10 disabled:to-white/10 disabled:text-slate-500 text-white text-xs font-semibold px-3 py-2 rounded-xl">
            <Banknote className="w-3.5 h-3.5"/> {owed > 0 ? "Pagar" : "Al día"}
          </button>
        </div>
      )}
      {/* Edit button bottom-right (always visible on hover) */}
      <button onClick={onEdit} data-testid={`edit-card-${card.id}`}
        className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 bg-white/15 hover:bg-white/25 text-white p-1.5 rounded-lg transition-all">
        <Edit2 className="w-3.5 h-3.5"/>
      </button>
    </div>
  );
}

/** Lo que se debe hoy en la tarjeta. En modo `independent` el saldo vive por
 *  divisa en buckets; en `single`/`shared` está colapsado en `balance_used`. */
function cardOwed(card, currency) {
  if ((card.currency_mode || "single") === "independent") {
    const buckets = card.balance_used_by_currency || {};
    if (currency) return Number(buckets[currency] || 0);
    return Object.values(buckets).reduce((a, b) => a + Number(b || 0), 0);
  }
  return Number(card.balance_used || 0);
}

function AddCardDialog({ open, onOpenChange, accounts, onCreated }) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState("credit");
  const [last4, setLast4] = useState("");
  const [brand, setBrand] = useState("Visa");
  const [bank, setBank] = useState("");
  const [currency, setCurrency] = useState("DOP");
  const [limit, setLimit] = useState("");
  const [overdraft, setOverdraft] = useState("");
  const [currencyMode, setCurrencyMode] = useState("single");
  const [limitUSD, setLimitUSD] = useState("");
  const [closingDay, setClosingDay] = useState("");
  const [paymentDueDay, setPaymentDueDay] = useState("");
  const [accountId, setAccountId] = useState("");
  const [color, setColor] = useState("#8B5CF6");
  const [initialDebt, setInitialDebt] = useState("");  // current outstanding balance at creation
  const colors = ["#8B5CF6", "#38BDF8", "#10B981", "#F59E0B", "#F43F5E", "#EC4899"];

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name, kind, last4, brand, currency, color,
      };
      if (kind === "credit") {
        payload.bank = bank;
        payload.limit = Number(limit || 0);
        payload.overdraft_limit = Number(overdraft || 0);
        payload.closing_day = Number(closingDay);
        payload.payment_due_day = Number(paymentDueDay);
        payload.currency_mode = currencyMode;
        payload.initial_balance_used = Number(initialDebt || 0);
        if (currencyMode === "independent") {
          payload.multi_currency_limits = {
            [currency]: Number(limit || 0),
            USD: Number(limitUSD || 0),
          };
        }
      } else {
        payload.account_id = accountId || accounts[0]?.id;
      }
      await api.post("/cards", payload);
      toast.success("Tarjeta agregada");
      setName(""); setLast4(""); setLimit(""); setBank(""); setOverdraft("");
      setLimitUSD(""); setClosingDay(""); setPaymentDueDay(""); setInitialDebt("");
      onCreated?.(); onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#15141b] border-white/10 text-white sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="add-card-dialog">
        <DialogHeader><DialogTitle className="text-white">Nueva tarjeta</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          {/* Kind selector */}
          <div className="grid grid-cols-2 gap-2 p-1 bg-white/5 rounded-xl">
            <button type="button" onClick={() => setKind("credit")} data-testid="cardform-kind-credit"
              className={`py-2.5 rounded-lg text-sm font-medium ${kind === "credit" ? "bg-violet-500/20 text-violet-200" : "text-slate-400"}`}>Crédito</button>
            <button type="button" onClick={() => setKind("debit")} data-testid="cardform-kind-debit"
              className={`py-2.5 rounded-lg text-sm font-medium ${kind === "debit" ? "bg-emerald-500/20 text-emerald-200" : "text-slate-400"}`}>Débito</button>
          </div>

          <FieldRow label="Nombre">
            <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Ej: Visa Popular Black" data-testid="cardform-name"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm"/>
          </FieldRow>

          {kind === "credit" && (
            <FieldRow label="Banco emisor">
              <input value={bank} onChange={(e) => setBank(e.target.value)} required placeholder="Ej: Banco Popular" data-testid="cardform-bank"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm"/>
            </FieldRow>
          )}

          <div className="grid grid-cols-3 gap-2">
            <FieldRow label="Marca">
              <select value={brand} onChange={(e) => setBrand(e.target.value)} data-testid="cardform-brand" className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm">
                {["Visa", "Mastercard", "American Express", "Discover"].map(b => <option key={b} className="bg-[#15141b]">{b}</option>)}
              </select>
            </FieldRow>
            <FieldRow label="Últimos 4">
              <input value={last4} onChange={(e) => setLast4(e.target.value.replace(/\D/g, "").slice(0, 4))} required maxLength={4} pattern="\d{4}" placeholder="0000" data-testid="cardform-last4"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm font-mono tracking-widest text-center"/>
            </FieldRow>
            <FieldRow label="Divisa">
              <select value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="cardform-currency" className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm">
                {["DOP","USD","EUR","USDT","MXN","COP","ARS","CLP","PEN","BRL"].map(c => <option key={c} className="bg-[#15141b]">{c}</option>)}
              </select>
            </FieldRow>
          </div>

          {kind === "credit" ? (
            <>
              <FieldRow label="Modo de divisa">
                <div className="grid grid-cols-3 gap-2 p-1 bg-white/5 rounded-xl">
                  {[
                    { v: "single", l: "Una divisa" },
                    { v: "shared", l: "Compartido" },
                    { v: "independent", l: "Independiente" },
                  ].map(o => (
                    <button key={o.v} type="button" onClick={() => setCurrencyMode(o.v)} data-testid={`cardform-mode-${o.v}`}
                      className={`py-2 rounded-lg text-xs font-medium ${currencyMode === o.v ? "bg-violet-500/20 text-violet-200" : "text-slate-400"}`}>{o.l}</button>
                  ))}
                </div>
                <p className="text-[10px] text-slate-500 mt-1">
                  {currencyMode === "single" && "Un solo límite en " + currency}
                  {currencyMode === "shared" && "Límite compartido entre Pesos y Dólares"}
                  {currencyMode === "independent" && "Límites separados por divisa"}
                </p>
              </FieldRow>

              <div className="grid grid-cols-2 gap-2">
                <FieldRow label={currencyMode === "independent" ? `Límite ${currency}` : "Límite total"}>
                  <input type="number" value={limit} onChange={(e) => setLimit(e.target.value)} required min="1" placeholder="50000" data-testid="cardform-limit"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm"/>
                </FieldRow>
                <FieldRow label="Sobregiro (opc.)">
                  <input type="number" value={overdraft} onChange={(e) => setOverdraft(e.target.value)} min="0" placeholder="0" data-testid="cardform-overdraft"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm"/>
                </FieldRow>
              </div>

              {currencyMode === "independent" && currency !== "USD" && (
                <FieldRow label="Límite USD">
                  <input type="number" value={limitUSD} onChange={(e) => setLimitUSD(e.target.value)} min="0" placeholder="1000" data-testid="cardform-limit-usd"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm"/>
                </FieldRow>
              )}

              <div className="grid grid-cols-2 gap-2">
                <FieldRow label="Día de corte">
                  <input type="number" min="1" max="31" value={closingDay} onChange={(e) => setClosingDay(e.target.value)} required placeholder="25" data-testid="cardform-closing-day"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm"/>
                </FieldRow>
                <FieldRow label="Día de pago">
                  <input type="number" min="1" max="31" value={paymentDueDay} onChange={(e) => setPaymentDueDay(e.target.value)} required placeholder="15" data-testid="cardform-payment-day"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm"/>
                </FieldRow>
              </div>

              <FieldRow label="Deuda actual de la tarjeta (opcional)">
                <input type="number" value={initialDebt} onChange={(e) => setInitialDebt(e.target.value)} min="0" placeholder="0.00" data-testid="cardform-initial-debt"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm"/>
                <p className="text-[10px] text-slate-500 mt-1">Si tu tarjeta ya tiene un saldo pendiente cuando empiezas a usar la app, ponlo aquí. Es solo el punto de partida — no genera ninguna transacción.</p>
              </FieldRow>
            </>
          ) : (
            <FieldRow label="Cuenta asociada (requerida)">
              <select value={accountId || accounts[0]?.id || ""} onChange={(e) => setAccountId(e.target.value)} required data-testid="cardform-account"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm">
                <option value="" className="bg-[#15141b]">Selecciona cuenta...</option>
                {accounts.map(a => <option key={a.id} value={a.id} className="bg-[#15141b]">{a.name} · {a.currency}</option>)}
              </select>
              <p className="text-[10px] text-slate-500 mt-1">Los gastos con esta tarjeta debitarán de esta cuenta.</p>
            </FieldRow>
          )}

          <FieldRow label="Color">
            <div className="flex gap-2">
              {colors.map(c => (
                <button key={c} type="button" onClick={() => setColor(c)} className={`w-8 h-8 rounded-full ${color === c ? "ring-2 ring-white ring-offset-2 ring-offset-[#15141b]" : ""}`} style={{ background: c }}/>
              ))}
            </div>
          </FieldRow>

          <button type="submit" data-testid="cardform-submit" className="w-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-semibold py-3 rounded-xl shadow-[0_8px_30px_-10px_rgba(139,92,246,0.6)]">Crear tarjeta</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FieldRow({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-1.5">{label}</div>
      {children}
    </div>
  );
}

function EditCardDialog({ card, onClose, onSaved }) {
  const isCredit = card.kind === "credit";
  const [name, setName] = useState(card.name || "");
  const [bank, setBank] = useState(card.bank || "");
  const [last4, setLast4] = useState(card.last4 || "");
  const [brand, setBrand] = useState(card.brand || "Visa");
  const [currency, setCurrency] = useState(card.currency || "DOP");
  const [limit, setLimit] = useState(String(card.limit || ""));
  const [overdraft, setOverdraft] = useState(String(card.overdraft_limit || ""));
  const [closingDay, setClosingDay] = useState(String(card.closing_day || ""));
  const [paymentDueDay, setPaymentDueDay] = useState(String(card.payment_due_day || ""));
  const [color, setColor] = useState(card.color || "#8B5CF6");
  const [currencyMode, setCurrencyMode] = useState(card.currency_mode || "single");
  const [balanceUsed, setBalanceUsed] = useState(String(card.balance_used || 0));
  const initLimits = card.multi_currency_limits || { [currency]: card.limit || 0, USD: 0 };
  const initBuckets = card.balance_used_by_currency || { [currency]: 0, USD: 0 };
  const [limitsByCur, setLimitsByCur] = useState({
    DOP: String(initLimits.DOP ?? 0),
    USD: String(initLimits.USD ?? 0),
  });
  const [bucketsByCur, setBucketsByCur] = useState({
    DOP: String(initBuckets.DOP ?? 0),
    USD: String(initBuckets.USD ?? 0),
  });
  const [loading, setLoading] = useState(false);
  // Motivo del ajuste de deuda. Obligatorio si cambia: una corrección sin
  // explicación es indistinguible de un error de la app seis meses después.
  const [adjustReason, setAdjustReason] = useState("");
  const colors = ["#8B5CF6", "#38BDF8", "#10B981", "#F59E0B", "#F43F5E", "#EC4899"];

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { name, brand, last4, color };
      if (isCredit) {
        payload.bank = bank;
        payload.currency = currency;
        payload.limit = Number(limit || 0);
        payload.overdraft_limit = Number(overdraft || 0);
        payload.closing_day = Number(closingDay);
        payload.payment_due_day = Number(paymentDueDay);
        payload.currency_mode = currencyMode;
        if (currencyMode === "independent") {
          payload.multi_currency_limits = {
            DOP: Number(limitsByCur.DOP || 0),
            USD: Number(limitsByCur.USD || 0),
          };
        }
      }

      // Lo que debes NO viaja en el PATCH: escribirlo directo reescribía la
      // deuda sin dejar rastro, y hacía inútil cualquier verificación (toda
      // diferencia podía explicarse con «alguien lo editó»). Va por su propio
      // endpoint, que exige un motivo y deja un movimiento auditable.
      const debtChanges = [];
      if (isCredit) {
        if (currencyMode === "independent") {
          for (const cur of ["DOP", "USD"]) {
            const next = Number(bucketsByCur[cur] || 0);
            const now = Number((card.balance_used_by_currency || {})[cur] || 0);
            if (Math.abs(next - now) >= 0.01) debtChanges.push({ currency: cur, next });
          }
        } else {
          const next = Number(balanceUsed || 0);
          if (Math.abs(next - Number(card.balance_used || 0)) >= 0.01) {
            debtChanges.push({ currency: undefined, next });
          }
        }
      }
      if (debtChanges.length && !adjustReason.trim()) {
        setLoading(false);
        return toast.error("Explica por qué cambia lo que debes (queda registrado)");
      }

      await api.patch(`/cards/${card.id}`, payload);
      for (const ch of debtChanges) {
        await api.post(`/cards/${card.id}/adjust-balance`, {
          new_balance: ch.next, reason: adjustReason.trim(),
          ...(ch.currency ? { currency: ch.currency } : {}),
        });
      }
      toast.success(debtChanges.length ? "Tarjeta actualizada y saldo ajustado" : "Tarjeta actualizada");
      setAdjustReason("");
      onSaved?.();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#15141b] border-white/10 text-white sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="edit-card-dialog">
        <DialogHeader><DialogTitle className="text-white">Editar tarjeta</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <FieldRow label="Nombre">
            <input value={name} onChange={(e) => setName(e.target.value)} required data-testid="edit-card-name"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm"/>
          </FieldRow>
          {isCredit && (
            <FieldRow label="Banco emisor">
              <input value={bank} onChange={(e) => setBank(e.target.value)} required data-testid="edit-card-bank"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm"/>
            </FieldRow>
          )}
          <div className="grid grid-cols-3 gap-2">
            <FieldRow label="Marca">
              <select value={brand} onChange={(e) => setBrand(e.target.value)} data-testid="edit-card-brand" className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm">
                {["Visa","Mastercard","American Express","Discover"].map(b => <option key={b} className="bg-[#15141b]">{b}</option>)}
              </select>
            </FieldRow>
            <FieldRow label="Últimos 4">
              <input value={last4} onChange={(e) => setLast4(e.target.value.replace(/\D/g,"").slice(0,4))} maxLength={4} required data-testid="edit-card-last4"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm font-mono tracking-widest text-center"/>
            </FieldRow>
            <FieldRow label="Divisa principal">
              <select value={currency} onChange={(e) => setCurrency(e.target.value)} disabled={!isCredit} data-testid="edit-card-currency"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm disabled:opacity-60">
                {["DOP","USD","EUR","USDT","MXN","COP","ARS","CLP","PEN","BRL"].map(c => <option key={c} className="bg-[#15141b]">{c}</option>)}
              </select>
            </FieldRow>
          </div>

          {isCredit && (
            <>
              <FieldRow label="Modo de divisa">
                <div className="grid grid-cols-3 gap-2 p-1 bg-white/5 rounded-xl">
                  {[
                    { v: "single", l: "Una divisa", tip: "Sólo " + currency },
                    { v: "shared", l: "Compartido", tip: "Cargos USD se convierten a " + currency },
                    { v: "independent", l: "Independiente", tip: "Saldos separados por divisa" },
                  ].map(o => (
                    <button key={o.v} type="button" onClick={() => setCurrencyMode(o.v)} data-testid={`edit-card-mode-${o.v}`}
                      className={`py-2 rounded-lg text-xs font-medium ${currencyMode === o.v ? "bg-violet-500/20 text-violet-200" : "text-slate-400"}`}>{o.l}</button>
                  ))}
                </div>
                <p className="text-[10px] text-slate-500 mt-1">
                  {currencyMode === "single" && `Un solo límite y saldo en ${currency}`}
                  {currencyMode === "shared" && `Un solo saldo; cargos en otras divisas se convierten a ${currency} con la tasa del día`}
                  {currencyMode === "independent" && "Cada divisa tiene su propio límite y saldo (ideal para tarjetas DOP+USD reales)"}
                </p>
              </FieldRow>

              {currencyMode !== "independent" ? (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <FieldRow label="Límite total">
                      <input type="number" value={limit} onChange={(e) => setLimit(e.target.value)} required min="1" data-testid="edit-card-limit"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm"/>
                    </FieldRow>
                    <FieldRow label="Sobregiro">
                      <input type="number" value={overdraft} onChange={(e) => setOverdraft(e.target.value)} min="0" data-testid="edit-card-overdraft"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm"/>
                    </FieldRow>
                  </div>
                  <FieldRow label={`Deuda actual (en ${currency})`}>
                    <input type="number" value={balanceUsed} onChange={(e) => setBalanceUsed(e.target.value)} min="0" data-testid="edit-card-balance-used"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm"/>
                    <p className="text-[10px] text-slate-500 mt-1">Corrige aquí el saldo pendiente de la tarjeta.</p>
                  </FieldRow>
                </>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {["DOP", "USD"].map(cur => (
                    <div key={cur} className="bg-white/5 rounded-xl p-3 border border-white/5 space-y-2">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-violet-300">{cur}</div>
                      <div>
                        <div className="text-[10px] text-slate-500 mb-1">Límite</div>
                        <input type="number" value={limitsByCur[cur]} onChange={(e) => setLimitsByCur({...limitsByCur, [cur]: e.target.value})} min="0" data-testid={`edit-card-limit-${cur}`}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-2 text-white text-sm"/>
                      </div>
                      <div>
                        <div className="text-[10px] text-slate-500 mb-1">Deuda actual</div>
                        <input type="number" value={bucketsByCur[cur]} onChange={(e) => setBucketsByCur({...bucketsByCur, [cur]: e.target.value})} min="0" data-testid={`edit-card-bucket-${cur}`}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-2 text-white text-sm"/>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Si cambia lo que debes, hace falta decir por qué: queda como
                  movimiento auditable, no como una escritura silenciosa. */}
              <FieldRow label="Motivo del cambio de deuda (si la cambiaste)">
                <input value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)}
                  placeholder="Ej: saldo inicial mal cargado" data-testid="edit-card-adjust-reason"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm"/>
              </FieldRow>

              <div className="grid grid-cols-2 gap-2">
                <FieldRow label="Día de corte">
                  <input type="number" min="1" max="31" value={closingDay} onChange={(e) => setClosingDay(e.target.value)} required data-testid="edit-card-closing"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm"/>
                </FieldRow>
                <FieldRow label="Día de pago">
                  <input type="number" min="1" max="31" value={paymentDueDay} onChange={(e) => setPaymentDueDay(e.target.value)} required data-testid="edit-card-payment-day"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm"/>
                </FieldRow>
              </div>
            </>
          )}

          <FieldRow label="Color">
            <div className="flex gap-2">
              {colors.map(c => (
                <button key={c} type="button" onClick={() => setColor(c)} data-testid={`edit-card-color-${c.slice(1)}`}
                  className={`w-8 h-8 rounded-full ${color === c ? "ring-2 ring-white ring-offset-2 ring-offset-[#15141b]" : ""}`} style={{ background: c }}/>
              ))}
            </div>
          </FieldRow>

          <button type="submit" disabled={loading} data-testid="edit-card-submit"
            className="w-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-semibold py-3 rounded-xl">
            {loading ? "Guardando…" : "Guardar cambios"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Abono al estado de cuenta de una tarjeta de crédito.
 *  No se ofrece "pagar con tarjeta de crédito": eso no salda la deuda, la mueve
 *  de una tarjeta a otra. El backend lo rechaza igual. */
function PayCardDialog({ card, accounts, cards, onClose, onPaid }) {
  const isIndependent = (card.currency_mode || "single") === "independent";
  const buckets = Object.entries(card.balance_used_by_currency || {}).filter(([, v]) => Number(v) > 0);
  const [currency, setCurrency] = useState(isIndependent && buckets.length ? buckets[0][0] : (card.currency || "DOP"));
  const [method, setMethod] = useState("account");
  const [accountId, setAccountId] = useState("");
  const [cardId, setCardId] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const owed = cardOwed(card, isIndependent ? currency : undefined);
  const debitCards = cards.filter(c => c.kind === "debit");

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Monto inválido");
    if (amt > owed) return toast.error("No puedes abonar más de lo que debes");
    setLoading(true);
    try {
      const { data } = await api.post(`/cards/${card.id}/pay`, {
        amount: amt, method, note, currency,
        account_id: method === "account" ? accountId : undefined,
        card_id: method === "debit_card" ? cardId : undefined,
      });
      toast.success(`Pago registrado · quedan ${fmtMoney(data.remaining, sym(currency))}`);
      onPaid?.();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#15141b] border-white/10 text-white sm:max-w-md max-h-[92vh] overflow-y-auto" data-testid="pay-card-dialog">
        <DialogHeader><DialogTitle className="text-white">Pagar {card.name} ••••{card.last4}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-3 text-sm">
            <span className="text-slate-400">Saldo usado:</span>{" "}
            <span className="text-rose-300 font-bold">{fmtMoney(owed, sym(currency))}</span>
          </div>

          {isIndependent && buckets.length > 1 && (
            <div>
              <FieldRow label="Divisa a abonar">
                <select value={currency} onChange={(e) => { setCurrency(e.target.value); setAmount(""); }} data-testid="pay-card-currency"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm">
                  {buckets.map(([cur, used]) => (
                    <option key={cur} value={cur} className="bg-[#15141b]">{cur} · {fmtMoney(used, sym(cur))}</option>
                  ))}
                </select>
              </FieldRow>
            </div>
          )}

          <FieldRow label="Método de pago">
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "account", l: "Cuenta", icon: Send },
                { v: "debit_card", l: "Débito", icon: CardIcon },
                { v: "cash", l: "Efectivo", icon: Banknote },
              ].map(o => (
                <button key={o.v} type="button" onClick={() => setMethod(o.v)} data-testid={`pay-card-method-${o.v}`}
                  className={`flex items-center justify-center gap-1.5 p-3 rounded-xl border text-xs font-medium ${method === o.v ? "bg-violet-500/20 border-violet-500/40 text-violet-200" : "bg-white/5 border-white/10 text-slate-400"}`}>
                  <o.icon className="w-4 h-4"/> {o.l}
                </button>
              ))}
            </div>
          </FieldRow>

          {method === "account" && (
            <FieldRow label="Desde cuenta">
              <select value={accountId} onChange={(e) => setAccountId(e.target.value)} required data-testid="pay-card-account"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm">
                <option value="" className="bg-[#15141b]">Selecciona...</option>
                {accounts.map(a => (
                  <option key={a.id} value={a.id} className="bg-[#15141b]">{a.name} · {fmtMoney(a.balance, sym(a.currency))}</option>
                ))}
              </select>
            </FieldRow>
          )}

          {method === "debit_card" && (
            <FieldRow label="Tarjeta de débito">
              <select value={cardId} onChange={(e) => setCardId(e.target.value)} required data-testid="pay-card-debit"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm">
                <option value="" className="bg-[#15141b]">Selecciona...</option>
                {debitCards.map(c => <option key={c.id} value={c.id} className="bg-[#15141b]">{c.name} ••••{c.last4}</option>)}
              </select>
            </FieldRow>
          )}

          <FieldRow label={`Monto del abono (${currency})`}>
            <input type="number" step="0.01" min="0.01" max={owed} value={amount} onChange={(e) => setAmount(e.target.value)} required data-testid="pay-card-amount"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-3 text-white text-xl font-bold"/>
          </FieldRow>

          <button type="button" onClick={() => setAmount(String(owed))} data-testid="pay-card-full"
            className="text-xs text-violet-300 hover:text-violet-200 font-medium">
            Pagar el total ({fmtMoney(owed, sym(currency))})
          </button>

          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota (opcional)" data-testid="pay-card-note"
            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm"/>

          <button type="submit" disabled={loading} data-testid="pay-card-submit"
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-violet-700 text-white font-semibold py-3 rounded-xl">
            {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : "Confirmar pago"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
